// @flow
/* eslint-disable react/display-name */

import React, { memo, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { debounce, throttle } from 'throttle-debounce'
import { findIndex, propEq, equals, head, reject } from 'ramda'
import { isNilOrEmpty } from 'ramda-adjunct'

import { scale } from '@alphaott/smart-tv-utils'
import { usePrevious } from '@alphaott/common-utils'

import { ListComponent } from '@alphaott/smart-tv-components'
import { useFocusable, useLastFocusHistory } from '@alphaott/smart-tv-spatial-navigation'
import { BUTTON_KEY_NAMES } from '@alphaott/smart-tv-platforms'

import { Container, Heading, Title, CHANNEL_LIST_CONTROL_WIDTH } from './elements'
import { ChannelCardHorizontal, BackButton } from './partials'
import { switchChannelCallback, findNextChannel, findPreviousChannel } from './utils'

export type ChannelsListControlProps = {
  isShowListControl: boolean,
  isShowSubtitlesSettings: boolean,
  title?: string,
  items: Array<any>,
  onSelectChannelsListItem: Function,
  onLongPressItem: Function,
  onCloseChannelsList: Function,
  activeChannelId: string,
  channelCardPlaceholder?: string,
  onBackPress: Function,
}

const SWITCH_CHANNEL_DELAY = 1000
const SWITCH_CHANNEL_PRELOAD_DELAY = 800
const CHANNEL_CARD_HEIGHT = 111
const HEADER_HEIGHT = 150

export const ChannelListControlFocusable = ({ title, items, activeChannelId, renderItem }) => {
  const { ref, setFocus } = useFocusable()
  const { t } = useTranslation()

  const listTitle = title || t('channels')
  const getHeight = useCallback((height) => height - scale(window.innerWidth, HEADER_HEIGHT), [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const height = useMemo(() => getHeight(window.innerHeight), [])
  const getItemSize = useCallback(() => scale(window.innerWidth, CHANNEL_CARD_HEIGHT), [])
  const initialScrollOffset = useMemo(() => {
    const itemIndex = findIndex(propEq('id', activeChannelId))(items)
    if (itemIndex === -1) return 0

    const itemSize = getItemSize()
    const offset = itemIndex * itemSize
    const centerOffset = offset - height / 2 + itemSize / 2

    return Math.max(0, centerOffset)
  }, [activeChannelId, items, height, getItemSize])
  const activeChannelIndex = items.findIndex((item) => item.id === activeChannelId)
  const firstItemId = head(items?.filter((item) => item?.id !== 'backButton'))?.id

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (setFocus) {
      const focusKey = activeChannelIndex === -1 ? firstItemId : activeChannelId

      if (focusKey) {
        setFocus(focusKey)
      }
    }
  }, [setFocus]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container ref={ref}>
      <Heading>
        <Title>{listTitle}</Title>
      </Heading>
      <div style={{ height }}>
        <ListComponent
          items={items}
          initialScrollOffset={initialScrollOffset}
          listWidth={CHANNEL_LIST_CONTROL_WIDTH}
          itemSize={CHANNEL_CARD_HEIGHT}
          renderItem={renderItem}
          layout="vertical"
        />
      </div>
    </Container>
  )
}

// $FlowFixMe
export const ChannelListControlPure = ({
  isShowListControl,
  isShowSubtitlesSettings,
  title,
  items,
  onSelectChannelsListItem,
  onLongPressItem,
  onCloseChannelsList,
  activeChannelId,
  channelCardPlaceholder,
  onBackPress,
}: ChannelsListControlProps) => {
  const { ref, setFocus } = useFocusable({ focusable: false })

  const itemsWithoutBackButton = reject((item) => item.id === 'backButton', items)
  const prevItemsWithoutBackButton = usePrevious(itemsWithoutBackButton)

  const { onSaveLastFocus } = useLastFocusHistory()

  const handleSelectTVListItem = useCallback(
    (item) => () => {
      onSelectChannelsListItem(item)
    },
    [onSelectChannelsListItem],
  )

  const handleLongPressItem = useCallback(
    (item) => () => {
      onLongPressItem(item)
      onSaveLastFocus({ focusKey: item.id })
    },
    [onLongPressItem, onSaveLastFocus],
  )

  const updateChannelIdDebounce = useMemo(
    () => debounce(SWITCH_CHANNEL_DELAY, (id, onSwitchChannel) => onSwitchChannel({ id })),
    [],
  )

  const updateChannelIdThrottle = useMemo(
    () =>
      throttle(SWITCH_CHANNEL_PRELOAD_DELAY, (preloadId, onSwitchChannel) =>
        onSwitchChannel({ preloadId }),
      ),
    [],
  )

  const handleChannelUp = useCallback(() => {
    const nextChannel = switchChannelCallback(
      activeChannelId,
      isNilOrEmpty(prevItemsWithoutBackButton)
        ? itemsWithoutBackButton
        : prevItemsWithoutBackButton,
    )(findNextChannel)

    updateChannelIdThrottle(nextChannel?.id, onSelectChannelsListItem)
    updateChannelIdDebounce(nextChannel?.id, onSelectChannelsListItem)
  }, [
    activeChannelId,
    itemsWithoutBackButton,
    prevItemsWithoutBackButton,
    onSelectChannelsListItem,
    updateChannelIdThrottle,
    updateChannelIdDebounce,
  ])

  const handleChannelDown = useCallback(() => {
    const prevChannel = switchChannelCallback(
      activeChannelId,
      isNilOrEmpty(prevItemsWithoutBackButton)
        ? itemsWithoutBackButton
        : prevItemsWithoutBackButton,
    )(findPreviousChannel)

    updateChannelIdThrottle(prevChannel?.id, onSelectChannelsListItem)
    updateChannelIdDebounce(prevChannel?.id, onSelectChannelsListItem)
  }, [
    activeChannelId,
    itemsWithoutBackButton,
    prevItemsWithoutBackButton,
    onSelectChannelsListItem,
    updateChannelIdThrottle,
    updateChannelIdDebounce,
  ])

  const handleBecameFocused = useCallback(
    (item) => (props) => {
      item.onBecameFocused(props)
    },
    [],
  )

  useEffect(() => {
    if (!isShowListControl && !isShowSubtitlesSettings) {
      document?.addEventListener(BUTTON_KEY_NAMES.ARROW_UP, handleChannelUp)
      document?.addEventListener(BUTTON_KEY_NAMES.ARROW_DOWN, handleChannelDown)
      document?.addEventListener(BUTTON_KEY_NAMES.CHANNEL_UP, handleChannelUp)
      document?.addEventListener(BUTTON_KEY_NAMES.CHANNEL_DOWN, handleChannelDown)
    }

    return () => {
      if (!isShowListControl && !isShowSubtitlesSettings) {
        document?.removeEventListener(BUTTON_KEY_NAMES.ARROW_UP, handleChannelUp)
        document?.removeEventListener(BUTTON_KEY_NAMES.ARROW_DOWN, handleChannelDown)
        document?.removeEventListener(BUTTON_KEY_NAMES.CHANNEL_UP, handleChannelUp)
        document?.removeEventListener(BUTTON_KEY_NAMES.CHANNEL_DOWN, handleChannelDown)
      }
    }
  }, [isShowListControl, handleChannelUp, handleChannelDown, isShowSubtitlesSettings])

  const renderItem = useCallback(
    ({ item }) => {
      if (item.isBackButton) {
        return (
          <BackButton
            focusKey={item.id}
            title={item.title}
            onFocus={handleBecameFocused(item)}
            onPress={onBackPress}
            style={item?.style}
          />
        )
      }

      return (
        <ChannelCardHorizontal
          {...item}
          focusKey={item.id}
          isCardHorizontal
          placeholder={channelCardPlaceholder}
          onFocus={handleBecameFocused(item)}
          onPress={handleSelectTVListItem(item)}
          onLongPress={handleLongPressItem(item)}
          isActive={equals(activeChannelId, item.id)}
          onSwitchChannel={onSelectChannelsListItem}
          onCloseChannelsList={onCloseChannelsList}
          setFocus={setFocus}
        />
      )
    },
    [activeChannelId, handleLongPressItem], // eslint-disable-line react-hooks/exhaustive-deps
  )

  if (!isShowListControl) return null

  return (
    <div ref={ref}>
      <ChannelListControlFocusable
        title={title}
        items={items}
        activeChannelId={activeChannelId}
        renderItem={renderItem}
      />
    </div>
  )
}
export const ChannelListControl = memo<ChannelsListControlProps>(ChannelListControlPure)

export default ChannelListControl
